<template>
  <card-container
    :id="pageId"
    :class="pageId"
    :is-loading="isLoading"
    loading-message="Loading assignments..."
    :no-action="true"
  >
    <template #title>
      Active Assignments
    </template>

    <template #content>
      <table-view-container
        :page-id="pageId"
        :data="sortedRows"
        :table-columns="tableColumns"
      >
        <template #right-fixed-columns>
          <el-table-column
            fixed="right"
            label="Actions"
            width="130"
            align="center"
          >
            <template #default="scope">
              <el-button link type="primary" size="small" @click="openEdit(scope.row)">Edit</el-button>
              <el-button link type="danger" size="small" @click="confirmDelete(scope.row)">Delete</el-button>
            </template>
          </el-table-column>
        </template>
      </table-view-container>
    </template>

    <!-- Edit Dialog -->
    <el-dialog v-model="editDialogVisible" title="Edit Assignment" width="500px">
      <div class="row align-items-center mb-3">
        <label class="col-sm-4 control-label">Start Date</label>
        <div class="col-sm-8">
          <date-picker
            v-model="editForm.startDate"
            format="MM/DD/YYYY"
            value-format="YYYY-MM-DD"
            :required="true"
          />
        </div>
      </div>

      <div class="row align-items-center mb-3">
        <label class="col-sm-4 control-label">End Date</label>
        <div class="col-sm-8">
          <date-picker
            v-model="editForm.endDate"
            format="MM/DD/YYYY"
            value-format="YYYY-MM-DD"
            :required="true"
          />
        </div>
      </div>

      <template #footer>
        <el-button @click="editDialogVisible = false">Cancel</el-button>
        <el-button type="primary" :loading="isSaving" @click="saveEdit">Save</el-button>
      </template>
    </el-dialog>
  </card-container>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue';
import { keyBy, orderBy, pick } from 'lodash';
import moment from 'moment-timezone';
import { ElMessageBox, ElNotification } from 'element-plus';
import { TableColumnType } from '@/models/table';
import { fetchCoachingAssignments, CoachingAssignmentRow } from '@/services/coaching/fetchCoachingAssignments';
import { deleteCoachingAssignment } from '@/services/coaching/deleteCoachingAssignment';
import { updateCoachingAssignment } from '@/services/coaching/updateCoachingAssignment';
import { fetchCoachingAgents, CoachingAgent } from '@/services/coaching/fetchCoachingAgents';
import { useEventListener } from '@/use/eventListener';

const pageId = 'coaching-assignments-list';

const tableColumns: TableColumnType[] = [
  { field: 'operatorName', label: 'Operator', minWidth: 160 },
  { field: 'coachingFocus', label: 'Coaching Focus', minWidth: 180 },
  { field: 'startDate', label: 'Start Date', width: 120 },
  { field: 'endDate', label: 'End Date', width: 120 },
  { field: 'status', label: 'Status', width: 100, isCenterAlign: true },
];

const rows = ref<CoachingAssignmentRow[]>([]);
const coachingAgents = ref<CoachingAgent[]>([]);
const agentById = computed(() => keyBy(coachingAgents.value, 'id'));
const isLoading = ref(false);

const isActive = (row: CoachingAssignmentRow) => {
  const today = moment.utc().format('YYYY-MM-DD');
  return row.startDate <= today && today <= row.endDate;
};

const sortedRows = computed(() =>
  orderBy(rows.value, [(r) => isActive(r)], ['desc']).map((row) => ({
    ...row,
    operatorName: row.operator ? `${row.operator.firstName} ${row.operator.lastName}` : row.operatorId,
    coachingFocus: agentById.value[row.agentId]?.displayName ?? row.agentId,
    status: isActive(row) ? 'Active' : 'Inactive',
  })),
);

// Edit
const editDialogVisible = ref(false);
const isSaving = ref(false);
const editingId = ref<string>('');
const editForm = ref({ startDate: '', endDate: '' });

const openEdit = (row: CoachingAssignmentRow) => {
  editingId.value = String(row.id);
  editForm.value = pick(row, ['startDate', 'endDate']);
  editDialogVisible.value = true;
};

const saveEdit = async () => {
  if (!editForm.value.startDate || !editForm.value.endDate) {
    return ElNotification({ type: 'warning', message: 'Start date and end date are required.' });
  }
  if (editForm.value.startDate > editForm.value.endDate) {
    return ElNotification({ type: 'warning', message: 'Start date must be before end date.' });
  }
  isSaving.value = true;
  try {
    await updateCoachingAssignment(editingId.value, editForm.value);
    ElNotification({ type: 'success', message: 'Assignment updated.' });
    editDialogVisible.value = false;
    await loadAssignments();
  } catch {
    ElNotification({ type: 'error', message: 'Failed to update assignment.' });
  } finally {
    isSaving.value = false;
  }
};

// Delete
const confirmDelete = async (row: CoachingAssignmentRow) => {
  const operatorName = row.operator
    ? `${row.operator.firstName} ${row.operator.lastName}`
    : 'this operator';

  try {
    await ElMessageBox.confirm(
      `Remove ${agentById.value[row.agentId]?.displayName ?? row.agentId} assignment for ${operatorName}?`,
      'Delete Assignment',
      { confirmButtonText: 'Delete', cancelButtonText: 'Cancel', type: 'warning' },
    );
  } catch {
    return;
  }

  try {
    await deleteCoachingAssignment(String(row.id));
    ElNotification({ type: 'success', message: 'Assignment deleted.' });
    await loadAssignments();
  } catch {
    ElNotification({ type: 'error', message: 'Failed to delete assignment.' });
  }
};

const loadAssignments = async () => {
  isLoading.value = true;
  try {
    rows.value = await fetchCoachingAssignments();
  } finally {
    isLoading.value = false;
  }
};

useEventListener('coaching-assignment-created', loadAssignments);

onMounted(async () => {
  try {
    const [, agents] = await Promise.all([loadAssignments(), fetchCoachingAgents()]);
    coachingAgents.value = agents;
  } catch {
    ElNotification({ type: 'error', message: 'Failed to load coaching assignments.' });
  }
});
</script>
